// hooks/useSnapping.js

import { useCallback } from 'react';

/**
 * Хук для логики "привязки" (snapping).
 *
 * @param {string} snappingMode - может быть 'none', 'grid', 'points'
 * @param {number} gridSize - шаг сетки
 * @param {number} snapThreshold - "зона притяжения"
 * @param {Array} cylinders - список цилиндров
 * @param {Array} fittings - список фитингов
 * @param {Array} pipes - список труб
 * @param {number} zoom - текущий масштаб Stage
 *
 * @returns {function} getSnappedPosition(x, y, [id, width, height]) - функция, которая вычисляет "привязанную" позицию
 */
function useSnapping({ snappingMode, gridSize, snapThreshold, cylinders, fittings, pipes, stageRef, zoom }) {
  /**
   * Функция для привязки координат (x, y)
   * к сетке или объектам (зависит от snappingMode).
   */
  const getSnappedPosition = useCallback(
    (x, y, id = null, movingWidth = 0, movingHeight = 0) => {
      let snappedX = x;
      let snappedY = y;

      if (snappingMode === 'grid') {
        // Привязка к сетке
        snappedX = Math.round(x / gridSize) * gridSize;
        snappedY = Math.round(y / gridSize) * gridSize;
      } else if (snappingMode === 'points') {
        // Привязка по «красным точкам» (углы и середины сторон)
        // Дополнительно: осевая привязка (совмещение только X или только Y),
        // даже если точки далеко друг от друга по другой оси
        const z = zoom || 1;
        const effectiveThreshold = (snapThreshold || 0) * z;
        const allObjects = [...(cylinders || []), ...(fittings || []), ...(pipes || [])];

        const stage = stageRef?.current;
        const getGroupNodeById = (nodeId) => {
          if (!stage) return null;
          let node = stage.findOne(`#${nodeId}`);
          if (!node) return null;
          if (node.getClassName && node.getClassName() !== 'Group') node = node.getParent();
          return node;
        };

        // getTopLeftStage не используется, логика реализована через offsets/anchors

        const getAnchorLocalPoints = (object) => {
          const ox = object.x || 0;
          const oy = object.y || 0;
          const w = object.width || 0;
          const h = object.height || 0;
          
          // Если у объекта есть кастомные точки привязки, используем их
          if (object.snapPoints && Array.isArray(object.snapPoints)) {
            return object.snapPoints
              .filter(point => !point.hidden) // Фильтруем скрытые точки
              .map(point => {
                // snapPoints определяются как функции от (ox, oy, w, h)
                // или как относительные координаты (0..1)
                // или как абсолютные координаты в мм
                if (typeof point === 'function') {
                  return point(ox, oy, w, h);
                } else if (point.relative) {
                  // Относительные координаты (например, {x: 0.5, y: 0.5, relative: true})
                  return { 
                    x: ox + w * point.x, 
                    y: oy + h * point.y 
                  };
                } else if (point.absolute) {
                  // Абсолютные координаты в мм (например, {x: 35, y: 35, absolute: true})
                  // Точки задаются относительно начала объекта в его собственных единицах (мм)
                  return { 
                    x: ox + (point.x || 0), 
                    y: oy + (point.y || 0) 
                  };
                } else {
                  // Абсолютные смещения от (ox, oy) - старый формат для совместимости
                  return { 
                    x: ox + (point.x || 0), 
                    y: oy + (point.y || 0) 
                  };
                }
              });
          }
          
          // Стандартные 8 точек по умолчанию
          return [
            { x: ox, y: oy },                    // top-left
            { x: ox + w, y: oy },                // top-right
            { x: ox, y: oy + h },                // bottom-left
            { x: ox + w, y: oy + h },            // bottom-right
            { x: ox + w / 2, y: oy },            // top-middle
            { x: ox + w / 2, y: oy + h },        // bottom-middle
            { x: ox, y: oy + h / 2 },            // left-middle
            { x: ox + w, y: oy + h / 2 },        // right-middle
          ];
        };

        const getAnchorStagePoints = (object) => {
          const node = getGroupNodeById(object.id);
          const localPts = getAnchorLocalPoints(object);
          if (!node) return localPts; // без трансформа — считаем как есть
          const t = node.getAbsoluteTransform();
          return localPts.map((p) => t.point(p));
        };

        const getAnchorOffsetsFromTopLeft = (object) => {
          const node = getGroupNodeById(object.id);
          const localPts = getAnchorLocalPoints(object);
          if (!node) {
            // offsets в локальной системе равны разности с (x,y)
            return localPts.map((p) => ({ x: p.x - (object.x || 0), y: p.y - (object.y || 0) }));
          }
          const t = node.getAbsoluteTransform();
          const tl = t.point({ x: object.x || 0, y: object.y || 0 });
          return localPts.map((p) => {
            const sp = t.point(p);
            return { x: sp.x - tl.x, y: sp.y - tl.y };
          });
        };

        const movingObj = id ? allObjects.find((o) => o.id === id) : null;
        if (movingObj) {
          // Предвычисляем смещения якорных точек от top-left (в координатах Stage)
          const movingOffsets = getAnchorOffsetsFromTopLeft(movingObj); // [{x,y} x8]

          let best = null;   // полноточечная привязка (X и Y)
          let bestX = null;  // осевая привязка по X
          let bestY = null;  // осевая привязка по Y

          allObjects.forEach((object) => {
            if (object.id === id) return;
            const otherAnchors = getAnchorStagePoints(object); // массив точек Stage

            otherAnchors.forEach((otherPt) => {
              movingOffsets.forEach((off) => {
                // Текущая позиция соответствующей якорной точки двигаемого объекта
                const movingPtNow = { x: snappedX + off.x, y: snappedY + off.y };
                const dx = movingPtNow.x - otherPt.x;
                const dy = movingPtNow.y - otherPt.y;
                const dist = Math.hypot(dx, dy);

                // Полное совпадение точки (как было раньше)
                if (dist < effectiveThreshold) {
                  const snapTopLeft = { x: otherPt.x - off.x, y: otherPt.y - off.y };
                  if (!best || dist < best.delta) best = { delta: dist, snapTo: snapTopLeft };
                }

                // Новая логика: осевая привязка
                const absDx = Math.abs(dx);
                const absDy = Math.abs(dy);
                if (absDx < effectiveThreshold) {
                  // Совмещаем X якорной точки → выравниваем только X top-left
                  const snapTopLeftX = { x: otherPt.x - off.x, y: snappedY };
                  if (!bestX || absDx < bestX.delta) bestX = { delta: absDx, snapTo: snapTopLeftX };
                }
                if (absDy < effectiveThreshold) {
                  // Совмещаем Y якорной точки → выравниваем только Y top-left
                  const snapTopLeftY = { x: snappedX, y: otherPt.y - off.y };
                  if (!bestY || absDy < bestY.delta) bestY = { delta: absDy, snapTo: snapTopLeftY };
                }
              });
            });
          });

          // Применяем приоритет: сначала полноточечная, затем осевые независимо
          if (best) {
            snappedX = best.snapTo.x;
            snappedY = best.snapTo.y;
          } else {
            if (bestX) snappedX = bestX.snapTo.x;
            if (bestY) snappedY = bestY.snapTo.y;
          }
        }
      }
      // если snappingMode === 'none' - ничего не делаем, возвращаем как есть

      return { x: snappedX, y: snappedY };
    },
    [snappingMode, gridSize, snapThreshold, cylinders, fittings, pipes, stageRef, zoom]
  );

  // Можно возвращать объект с функциями или одну функцию
  return {
    getSnappedPosition,
  };
}

export default useSnapping;
